import type {
  CalendarInfo,
  CalendarInput,
  CalendarSyncResult,
  CalendarSyncTarget,
  EmailLinks,
  MailboxFile,
  MailboxFolder,
  MailboxMessage,
  MailboxMessageRef,
  MailboxPage,
  MailAccount,
  MailboxInput,
  SendEmailInput,
  SentEmail,
  SignatureInput,
} from "../../mail/mailTypes";
import { MailError } from "../../mail/mailTypes";
import type { RAFile } from "../../types";
import { getSupabaseClient } from "./supabase";

type Method = "GET" | "PUT" | "POST" | "DELETE";

/** Calls a route of the mail-sync service through the `mail` function. */
const invokeMail = async <T>(
  method: Method,
  path: string,
  body?: unknown,
): Promise<T> => {
  const { data, error } = await getSupabaseClient().functions.invoke<T>(
    `mail/${path}`,
    { method, body: body as Record<string, unknown> | undefined },
  );
  if (error) {
    const details = await (async () => {
      try {
        return (await error.context?.json()) ?? {};
      } catch {
        return {};
      }
    })();
    throw new MailError(details?.error ?? "unknown", details?.protocol);
  }
  return data as T;
};

/**
 * Mailbox and calendar methods of the Supabase data provider. `uploadFile`
 * stores the signature picture in the attachments bucket first.
 */
export const createMailMethods = (
  uploadFile: (file: RAFile) => Promise<RAFile>,
) => ({
  async getMailAccount(): Promise<MailAccount | null> {
    return invokeMail<MailAccount | null>("GET", "account");
  },
  async connectMailbox(input: MailboxInput): Promise<MailAccount> {
    return invokeMail<MailAccount>("PUT", "account/mailbox", input);
  },
  async disconnectMailbox(): Promise<MailAccount> {
    return invokeMail<MailAccount>("DELETE", "account/mailbox");
  },
  async connectCalendar(
    input: CalendarInput,
  ): Promise<{ account: MailAccount; calendars: CalendarInfo[] }> {
    return invokeMail("PUT", "account/calendar", input);
  },
  async listCalendars(): Promise<CalendarInfo[]> {
    const { calendars } = await invokeMail<{ calendars: CalendarInfo[] }>(
      "GET",
      "account/calendars",
    );
    return calendars;
  },
  async selectCalendar(url: string): Promise<MailAccount> {
    return invokeMail<MailAccount>("PUT", "account/calendar/selection", {
      url,
    });
  },
  async disconnectCalendar(): Promise<MailAccount> {
    return invokeMail<MailAccount>("DELETE", "account/calendar");
  },
  async saveMailSignature(input: SignatureInput): Promise<MailAccount> {
    return invokeMail<MailAccount>("PUT", "account/signature", {
      signature_text: input.signature_text,
      signature_image: input.signature_image
        ? await uploadFile(input.signature_image)
        : null,
    });
  },
  async testMailAccount(): Promise<MailAccount> {
    return invokeMail<MailAccount>("POST", "account/test");
  },
  /** Reads the new emails of the mailbox now, in the background. */
  async syncMailbox(): Promise<MailAccount> {
    return invokeMail<MailAccount>("POST", "account/sync");
  },
  // « Messagerie » page: the user's own mailbox, read live.
  async getMailboxFolders(): Promise<MailboxFolder[]> {
    return invokeMail<MailboxFolder[]>("POST", "mailbox/folders");
  },
  async getMailboxMessages(params: {
    folder: string;
    page: number;
    q: string;
  }): Promise<MailboxPage> {
    return invokeMail<MailboxPage>("POST", "mailbox/messages", params);
  },
  async getMailboxMessage(ref: MailboxMessageRef): Promise<MailboxMessage> {
    return invokeMail<MailboxMessage>("POST", "mailbox/message", ref);
  },
  async getMailboxAttachment(
    ref: MailboxMessageRef & { index: number },
  ): Promise<MailboxFile> {
    return invokeMail<MailboxFile>("POST", "mailbox/attachment", ref);
  },
  async deleteMailboxMessage(ref: MailboxMessageRef): Promise<void> {
    await invokeMail("POST", "mailbox/delete", ref);
  },
  /** « Ajouter au fil »: the message joins the feeds of these records. */
  async addMailboxMessageToCrm(
    ref: MailboxMessageRef & EmailLinks,
  ): Promise<SentEmail> {
    return invokeMail<SentEmail>("POST", "mailbox/add-to-crm", ref);
  },
  /** Puts the event(s) in the staff calendars, or sends their invitations. */
  async syncCalendar(target: CalendarSyncTarget): Promise<CalendarSyncResult> {
    return invokeMail<CalendarSyncResult>("POST", "calendar/sync", target);
  },
  /** Sends from the user's mailbox; new attachments are stored first. */
  async sendEmail(input: SendEmailInput): Promise<SentEmail> {
    const attachments = await Promise.all(
      input.attachments.map((file) => (file.path ? file : uploadFile(file))),
    );
    return invokeMail<SentEmail>("POST", "send", {
      ...input,
      attachments: attachments.map((file) => ({
        path: file.path,
        title: file.title,
        type: file.type,
        src: file.src,
      })),
    });
  },
});
